import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import ImageGallery from '../../../components/ImageGallery/ImageGallery';
import * as events from '../../../lib/analytics/events';
import { bandSeriesToCollarSeries } from '../../../lib/product';
import { cartActions as storeCartActions } from '../../../reducers/storeCart';
import * as types from '../../../types';
import { Series } from '../../../types/Series';
import AddToBagButton from '../components/AddToBagButton';
import BandSeriesToggle from '../components/BandSeriesToggle';
import BandVariantOptions from '../components/BandVariantOptions';
import ProductTitle from '../components/ProductTitle';
import SoldOutBanner from '../components/SoldOutBanner';
import useIsSkuBlocked from '../hooks/useIsSkuBlocked';
import useVariantSelector from '../hooks/useVariantSelector';
import styles from '../ProductDetails.module.scss';
import {
  collarProductIsSupportedInCountry,
  makersAccessoriesProductIsSupportedInCountry,
  useLocale,
} from '../../../contexts/LocaleContext';
import { BandSeries, series2CollarId } from '../../../types';
import qs from 'qs';

function useBandSeriesToProductIdMap() {
  return useSelector((state: types.AppState) => {
    const products = state.config.products;

    const map = new Map<Series, string>();
    const series2BandInProductConfig = !!products.find((product) => product.id === types.f1BandId);
    if (series2BandInProductConfig) {
      map.set(Series.Series2, types.f1BandId);
    }

    const series3BandInProductConfig = !!products.find((product) => product.id === types.f3BandId);
    if (series3BandInProductConfig) {
      map.set(Series.Series3, types.f3BandId);
    }

    return map;
  });
}

interface OnChangeHandlerProps {
  priceInCents: number;
  productName: string;
  currency: string;
}

function useOnChangeHandlers({ priceInCents, productName, currency }: OnChangeHandlerProps) {
  const onColorChange = useCallback(
    (newVariant: types.IVariant) => {
      events.bandColorSwitch({
        color: newVariant.options.color,
        priceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        currency,
      });
    },
    [priceInCents, productName, currency],
  );

  const onSizeChange = useCallback(
    (newVariant: types.IVariant) => {
      events.bandSizeSwitch({
        color: newVariant.options.color,
        priceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        currency,
      });
    },
    [priceInCents, productName, currency],
  );

  const onSelectedVariantChange = useCallback(
    (newVariant: types.IVariant) => {
      events.bandVariantSwitch({
        color: newVariant.options.color,
        priceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        currency,
      });
    },
    [priceInCents, productName, currency],
  );

  return {
    onColorChange,
    onSizeChange,
    onSelectedVariantChange,
  };
}

function selectedVariantImageUrl(productId: string, variant: types.IVariant) {
  return `/product_images/${productId}/${variant.options.color.replace(/[\s/]/g, '_')}.jpg`;
}

interface BandDetailsProps {
  bandSeries: types.BandSeries;
  priceInCents: number;
  productId: string;
  productName: string;
  variants: types.IVariant[];
}

export default function BandDetails({ bandSeries, priceInCents, productId, productName, variants }: BandDetailsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { country, currency } = useLocale();

  const { onColorChange, onSizeChange, onSelectedVariantChange } = useOnChangeHandlers({
    priceInCents,
    productName,
    currency,
  });

  const { changeVariantOptions, selectedVariant } = useVariantSelector({
    onColorChange,
    onSelectedVariantChange,
    onSizeChange,
    variants,
    useQueryParams: true,
  });
  const isBlocked = useIsSkuBlocked(selectedVariant?.sku);

  const seriesProductIdMap = useBandSeriesToProductIdMap();

  useEffect(() => {
    events.bandViewed();
  }, []);

  const currentBandSeries = collarProductIsSupportedInCountry(series2CollarId, country) ? bandSeries : BandSeries.F3;
  useEffect(() => {
    const newProductId = seriesProductIdMap.get(bandSeriesToCollarSeries(currentBandSeries));
    if (!newProductId) {
      return;
    }
    const queryString = qs.stringify(selectedVariant.options);
    const targetPath = `${AppPaths.Products(newProductId)}?${queryString}`;
    const currentPath = `${history.location.pathname}${history.location.search}`;
    if (currentPath !== targetPath) {
      history.replace(targetPath, {
        noScroll: true,
      });
    }
    // eslint-disable-next-line
  }, [currentBandSeries]);

  return (
    <>
      <div className={styles.productContainer}>
        <div className={styles.imageGalleryContainer}>
          <ImageGallery
            media={[
              {
                type: 'image',
                url: selectedVariantImageUrl(productId, selectedVariant),
                retinaWidth: 612,
                retinaHeight: 440,
              },
            ]}
          />
        </div>
        <div className={styles.productDetails}>
          <div className={styles.productDetailsHeader}>
            <div className={styles.productDetailsTitle}>
              <ProductTitle productName={productName} price={{ priceInCents }} />
            </div>
            <p className={styles.description}>
              {t(
                'this_is_an_additional_collar_without_a_tracker_why_well_you_wouldnt_wear_the_same_shirt_everyday_so_why_should_your_dog_have_to_wear_the_same_collar_all_the_time_spice_up_your_life',
              )}
            </p>
          </div>

          {collarProductIsSupportedInCountry(series2CollarId, country) && (
            <BandSeriesToggle
              seriesProductIdMap={seriesProductIdMap}
              selectedSeries={bandSeriesToCollarSeries(currentBandSeries)}
              variantOptions={selectedVariant.options}
            />
          )}

          <BandVariantOptions
            bandSeries={currentBandSeries}
            changeVariantOptions={changeVariantOptions}
            selectedVariant={selectedVariant}
            variants={variants}
            startSizeAsUnselected={false}
            hideMakersLink={!makersAccessoriesProductIsSupportedInCountry(country)}
          />

          {isBlocked && <SoldOutBanner />}

          <div className={styles.productAction}>
            <AddToBagButton
              disabled={isBlocked}
              onAddToBag={() => {
                if (isBlocked) {
                  return false;
                }

                events.bandAddedToBag({
                  color: selectedVariant.options.color,
                  priceInCents,
                  productName,
                  size: selectedVariant.options.size,
                  sku: selectedVariant.sku,
                  currency,
                });

                dispatch(
                  storeCartActions.addSingleLineCartItem({
                    sku: selectedVariant.sku,
                    currency,
                  }),
                );

                history.push(AppPaths.Bag);

                return true;
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
